import { useState } from 'react'
import { Link } from 'react-router-dom'
import MetaTags from '../components/common/MetaTags'
import ResumeUploadCard from '../components/common/ResumeUploadCard'
import { useProfileContext } from '../components/layout/AuthenticatedLayout'
import { useAuth } from '../hooks/useAuth'
import { profileService } from '../services/profileService'
import { getDisplayDomain, getProfileUrl } from '../utils/url'

export default function DashboardPage() {
  const { profile, setProfile } = useProfileContext()
  const { refreshUser } = useAuth()
  const siteName = getDisplayDomain()

  const [copied, setCopied] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isTogglingVisibility, setIsTogglingVisibility] = useState(false)

  const handleCopyUrl = async () => {
    if (!profile) return
    try {
      await navigator.clipboard.writeText(getProfileUrl(profile.username))
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  const handleDeleteProfile = async () => {
    setIsDeleting(true)
    try {
      await profileService.deleteProfile()
      await refreshUser()
      setProfile(null)
      window.location.href = '/'
    } catch (err) {
      console.error('Failed to delete profile:', err)
      setIsDeleting(false)
      setShowDeleteConfirm(false)
    }
  }

  const handleToggleVisibility = async () => {
    if (!profile) return
    setIsTogglingVisibility(true)
    try {
      const updatedProfile = await profileService.updateProfile({
        is_public: !profile.is_public,
      })
      setProfile(updatedProfile)
    } catch (err) {
      console.error('Failed to update visibility:', err)
    } finally {
      setIsTogglingVisibility(false)
    }
  }

  if (!profile) {
    return (
      <div className="max-w-2xl mx-auto">
        <MetaTags
          title="Dashboard | profile.work"
          description="Manage your professional profile on profile.work."
        />
        <ResumeUploadCard />
      </div>
    )
  }

  return (
    <div className="max-w-2xl mx-auto">
      <MetaTags
        title="Dashboard | profile.work"
        description="Manage your professional profile on profile.work."
      />

      {/* Header */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-black/10 rounded-full text-black text-sm font-medium mb-4">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          Profile Active
        </div>
        <h1
          className="text-2xl sm:text-3xl text-black mb-2"
          style={{ fontFamily: "'Instrument Serif', serif" }}
        >
          Welcome back, {profile.first_name}!
        </h1>
        <p className="text-black/60">Your profile is live and ready to share.</p>
      </div>

      {/* Profile Card */}
      <div className="bg-white/70 backdrop-blur-xl rounded-2xl shadow-xl border border-black/10 p-4 sm:p-6">
        {/* Profile Preview */}
        <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6 mb-6 pb-6 border-b border-black/10 text-center sm:text-left">
          {profile.profile_image_url ? (
            <img
              src={profile.profile_image_url}
              alt={profile.full_name}
              className="w-16 h-16 sm:w-20 sm:h-20 rounded-full object-cover flex-shrink-0 shadow-lg"
            />
          ) : (
            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-black rounded-full flex items-center justify-center text-white text-xl sm:text-2xl font-bold flex-shrink-0 shadow-lg">
              {profile.first_name[0]}
              {profile.last_name[0]}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-bold text-black truncate">{profile.full_name}</h2>
            {profile.headline && <p className="text-black/60 truncate">{profile.headline}</p>}
            <div className="flex items-center justify-center sm:justify-start gap-2 mt-1">
              <p className="text-sm text-black">
                {profile.username}.{siteName}
              </p>
              <button
                onClick={handleCopyUrl}
                className="p-1.5 text-black/40 hover:text-black hover:bg-black/5 rounded-lg transition-colors"
                title="Copy profile URL"
              >
                {copied ? (
                  <svg
                    className="w-4 h-4 text-black"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                    />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Visibility Toggle */}
        <div className="flex items-center justify-between mb-6 pb-6 border-b border-black/10">
          <div>
            <p className="text-sm font-medium text-black">Public Profile</p>
            <p className="text-xs text-black/60">
              {profile.is_public
                ? 'Your profile is visible to everyone'
                : 'Only you can see your profile'}
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={profile.is_public}
            onClick={handleToggleVisibility}
            disabled={isTogglingVisibility}
            className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
              profile.is_public ? 'bg-black' : 'bg-gray-200'
            }`}
          >
            <span
              className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                profile.is_public ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="text-center p-3 bg-black/5 rounded-xl">
            <p className="text-2xl font-bold text-black">{profile.work_experiences.length}</p>
            <p className="text-xs text-black/60">Experiences</p>
          </div>
          <div className="text-center p-3 bg-black/5 rounded-xl">
            <p className="text-2xl font-bold text-black">{profile.educations.length}</p>
            <p className="text-xs text-black/60">Education</p>
          </div>
          <div className="text-center p-3 bg-black/5 rounded-xl">
            <p className="text-2xl font-bold text-black">{profile.projects.length}</p>
            <p className="text-xs text-black/60">Projects</p>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="flex flex-col sm:flex-row gap-3">
          <a
            href={getProfileUrl(profile.username)}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 py-3 px-4 bg-gray-800 text-white font-medium rounded-xl hover:bg-gray-700 transition-all text-center text-sm"
          >
            View Profile
          </a>
          <Link
            to="/edit"
            className="flex-1 py-3 px-4 bg-white/50 border border-black/20 text-black font-medium rounded-xl hover:bg-black/5 transition-all text-center text-sm"
          >
            Edit Details
          </Link>
        </div>

        {/* Delete Profile */}
        <div className="mt-6 pt-4 border-t border-black/10 text-center">
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="text-sm text-red-600 hover:text-red-700 font-medium"
          >
            Delete Profile
          </button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-md"
            onClick={() => setShowDeleteConfirm(false)}
          />
          <div
            className="relative bg-white/70 backdrop-blur-xl rounded-2xl shadow-xl w-full max-w-md mx-4 p-6"
            style={{ animation: 'fadeInScale 0.2s ease-out' }}
          >
            <h3 className="text-lg font-semibold text-black mb-2">Delete Profile?</h3>
            <p className="text-black/60 mb-6">
              This will permanently delete your profile, including all work experiences, education,
              and projects. This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 py-2 px-4 bg-black/10 text-black font-medium rounded-lg hover:bg-black/20"
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteProfile}
                disabled={isDeleting}
                className="flex-1 py-2 px-4 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {isDeleting ? 'Deleting...' : 'Delete Profile'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Keyframe animation */}
      <style>{`
        @keyframes fadeInScale {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
      `}</style>
    </div>
  )
}
